import { Injectable } from '@angular/core';
import { EMPTY, Observable } from 'rxjs';
import {
  AssistantKey,
  cancelTestRunV2$,
  ConfigVoiceConfig,
  deleteTestCases$,
  deleteTestSuite$,
  generateTestCases$,
  GenerateTestCasesResponse,
  getTestRunV2$,
  getTestSuite$,
  GetTestSuiteResponse,
  listTestCasesByAssistant$,
  ListTestCasesByAssistantResponse,
  listTestResults$,
  ListTestResultsResponse,
  listTestRuns$,
  ListTestRunsResponse,
  listTestSuites$,
  ListTestSuitesResponse,
  retryStuckTestResults$,
  RetryStuckTestResultsResponse,
  runSuccessEvaluation$,
  RunSuccessEvaluationResponse,
  runTestsV2$,
  RunTestsV2Response,
  SortDirection,
  TestCase,
  TestCaseKey,
  TestLLMOptions,
  TestResult,
  TestRun,
  TestSuite,
  TestType,
  upsertTestCases$,
  upsertTestSuite$,
  UpsertTestSuiteResponse,
} from '@vendasta/protos/ai_assistants/v1alpha1';
import { PagedRequestOptionsInterface } from '@vendasta/galaxy/table';
import { PagedRequestOptions } from '@vendasta/protos/vendastatypes';
import { expand, map, reduce } from 'rxjs/operators';

function toProtoPagingOptions(options?: PagedRequestOptionsInterface): PagedRequestOptions {
  return {
    cursor: options?.cursor ?? '',
    pageSize: options?.pageSize ?? 0,
  };
}

export type SourceFilter =
  | { type: 'conversationId'; value: string }
  | { type: 'callRecordId'; value: string };

@Injectable({
  providedIn: 'root',
})
export class TestManagementService {
  upsertTestCases(assistantKey: Partial<AssistantKey>, cases: Partial<TestCase>[]): Observable<void> {
    return upsertTestCases$({ assistant: assistantKey, testCases: cases });
  }

  deleteTestCases(assistantKey: Partial<AssistantKey>, testCaseIDs?: string[]): Observable<void> {
    return deleteTestCases$({
      assistant: assistantKey,
      testCaseIds: testCaseIDs,
    });
  }

  listTestCasesByAssistant(
    assistantKey: Partial<AssistantKey>,
    pagingOptions: PagedRequestOptionsInterface,
    nameSearch: string,
    nameSortDirection: SortDirection,
    onlyEvaluators?: boolean,
  ): Observable<ListTestCasesByAssistantResponse> {
    return listTestCasesByAssistant$({
      searchTerm: nameSearch,
      sortOptions: {
        field: 'name',
        direction: nameSortDirection,
      },
      assistant: assistantKey,
      pagingOptions: toProtoPagingOptions(pagingOptions),
      onlyEvaluators: onlyEvaluators ?? false,
    });
  }

  getTestRunV2(testRunId: string, filter: { assistantKey?: Partial<AssistantKey>; testSuiteId?: string }): Observable<TestRun> {
    return getTestRunV2$({
      assistant: filter.assistantKey,
      id: testRunId,
      testSuiteId: filter.testSuiteId,
    }).pipe(map((response) => response?.testRun));
  }

  listTestResults(
    testRunId: string,
    filter: { assistantKey?: Partial<AssistantKey>; testSuiteId?: string },
    pagingOptions?: PagedRequestOptionsInterface,
  ): Observable<ListTestResultsResponse> {
    return listTestResults$({
      assistant: filter.assistantKey,
      testSuiteId: filter.testSuiteId,
      testRunId: testRunId,
      pagingOptions: toProtoPagingOptions(pagingOptions),
    });
  }

  fetchAllTestResults(
    testRunId: string,
    filter: { assistantKey?: Partial<AssistantKey>; testSuiteId?: string },
  ): Observable<TestResult[]> {
    const pageSize = 100;
    return this.listTestResults(testRunId, filter, { pageSize, cursor: '' }).pipe(
      expand((resp: ListTestResultsResponse) =>
        resp.pagingMetadata?.hasMore
          ? this.listTestResults(testRunId, filter, { pageSize, cursor: resp.pagingMetadata.nextCursor })
          : EMPTY,
      ),
      reduce((acc: TestResult[], resp: ListTestResultsResponse) => acc.concat(resp.testResults || []), []),
    );
  }

  cancelTestRun(
    testRunId: string,
    testRunType: { assistantKey?: Partial<AssistantKey>; testSuiteId?: string },
  ): Observable<void> {
    return cancelTestRunV2$({
      assistant: testRunType.assistantKey,
      id: testRunId,
      testSuiteId: testRunType.testSuiteId,
    });
  }

  retryStuckTestResults(
    testRunId: string,
    filter: { assistantKey?: Partial<AssistantKey>; testSuiteId?: string },
  ): Observable<RetryStuckTestResultsResponse> {
    return retryStuckTestResults$({
      assistant: filter.assistantKey,
      testRunId: testRunId,
      testSuiteId: filter.testSuiteId,
    });
  }

  // Test Suite methods
  listTestSuites(
    searchTerm?: string,
    tags?: string[],
    pagingOptions?: PagedRequestOptionsInterface,
  ): Observable<ListTestSuitesResponse> {
    if (!pagingOptions) {
      pagingOptions = { pageSize: 10, cursor: '' };
    }

    return listTestSuites$({
      searchTerm: searchTerm,
      tags: tags,
      pagingOptions: toProtoPagingOptions(pagingOptions),
    });
  }

  getTestSuite(id: string): Observable<GetTestSuiteResponse> {
    return getTestSuite$({ id: id });
  }

  upsertTestSuite(testSuite: Partial<TestSuite>): Observable<UpsertTestSuiteResponse> {
    return upsertTestSuite$({ testSuite: testSuite });
  }

  deleteTestSuite(id: string): Observable<void> {
    return deleteTestSuite$({ id: id });
  }

  // New listTestRuns that supports both assistant and suite filters
  listTestRuns(
    filter: { assistantKey?: Partial<AssistantKey>; testSuiteId?: string },
    pagingOptions?: PagedRequestOptionsInterface,
    onlySuccessEvaluations?: boolean,
    source?: SourceFilter,
  ): Observable<ListTestRunsResponse> {
    return listTestRuns$({
      assistant: filter.assistantKey,
      testSuiteId: filter.testSuiteId,
      pagingOptions: toProtoPagingOptions(pagingOptions),
      onlySuccessEvaluations: onlySuccessEvaluations ?? false,
      ...(source?.type === 'callRecordId' && { sourceCallRecordId: source.value }),
      ...(source?.type === 'conversationId' && { sourceConversationId: source.value }),
    });
  }

  generateTestCases(
    assistantKey: Partial<AssistantKey>,
    instructions: string,
    testType?: TestType,
  ): Observable<GenerateTestCasesResponse> {
    return generateTestCases$({
      assistant: assistantKey,
      instructions,
      testType: testType ?? 'TEST_TYPE_CHAT',
    });
  }

  runTestsV2(opts: RunTestsV2Options): Observable<RunTestsV2Response> {
    return runTestsV2$({
      assistant: opts.assistantKey,
      testSuiteId: opts.testSuiteId,
      testRunName: opts.testRunName,
      llmOptions: opts.llmOptions,
      voiceConfig: opts.voiceConfig,
      testCaseKeys: opts.testCaseKeys,
      iterations: opts.iterations,
    });
  }

  runSuccessEvaluation(
    assistantKey: Partial<AssistantKey>,
    source: { conversationId: string } | { callRecordId: string },
  ): Observable<RunSuccessEvaluationResponse> {
    return runSuccessEvaluation$({
      assistantKey,
      ...('conversationId' in source
        ? { conversationId: source.conversationId }
        : { callRecordId: source.callRecordId }),
    });
  }
}

export interface RunTestsV2Options {
  assistantKey?: Partial<AssistantKey>;
  testSuiteId?: string;
  testRunName?: string;
  llmOptions?: Partial<TestLLMOptions>;
  voiceConfig?: Partial<ConfigVoiceConfig>;
  testCaseKeys?: TestCaseKey[];
  iterations?: number;
}
